import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Seo } from "@/components/Seo";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Clock, Mail, Globe, MessageCircle, Send, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";
import BusinessCards from "@/components/BusinessCards";
import QuestionForm from "@/components/QuestionForm";
import LocationsMap from "@/components/LocationsMap";
import PaymentMethods from "@/components/PaymentMethods";
import { RequestTypeSwitch } from "@/components/RequestTypeSwitch";
import SlotPicker from "@/components/SlotPicker";
import PhotoPicker from "@/components/PhotoPicker";
import AiWriteButton from "@/components/AiWriteButton";
import { useSettings, waUrl } from "@/context/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full rounded-xl border border-brand/20 bg-white px-4 py-3.5 text-sm text-forest placeholder:text-brand/40 outline-none transition-all duration-300 focus:border-gold focus:ring-4 focus:ring-gold/10";
const labelClass = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-brand";

const InfoRow = ({ icon: Icon, label, children, orange }) => (
  <li className="flex items-start gap-4">
    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${orange ? "bg-cream text-gold" : "bg-mint text-brand"}`}>
      <Icon className="h-5 w-5" />
    </span>
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand">{label}</p>
      {children}
    </div>
  </li>
);

const Contact = () => {
  const { lang, t } = useLanguage();
  const { settings: st } = useSettings();
  const WHATSAPP_URL = waUrl(st);
  const f = t.contact.form;
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [slotRefresh, setSlotRefresh] = useState(0);
  const empty = { name: "", phone: "", email: "", service: "", vehicle: "", date: "", time: "", message: "", pickup_address: "", pickup_period: "" };
  const [params] = useSearchParams();
  const prefilled = !!params.get("prefill");
  const [form, setForm] = useState(() => prefilled ? { ...empty, name: params.get("name") || "", phone: params.get("phone") || "", email: params.get("email") || "", vehicle: params.get("vehicle") || "", service: params.get("service") || "" } : empty);
  const TYPES = ["devis", "rdv", "pickup", "question"];
  const paramType = TYPES.includes(params.get("type")) ? params.get("type") : "devis";
  const [type, setType] = useState(paramType);
  const isPickup = type === "pickup";
  const isDevis = type === "devis";
  const copy = { devis: { message: f.devisMessage, messagePh: f.devisMessagePh, submit: f.devisSubmit, success: f.devisSuccess, waIntro: f.devisWaIntro },
    rdv: { message: f.rdvMessage, messagePh: f.rdvMessagePh, submit: f.rdvSubmit, success: f.rdvSuccess, waIntro: f.rdvWaIntro },
    pickup: { message: f.pickupMessage, messagePh: f.pickupMessagePh, submit: f.pickupSubmit, success: f.pickupSuccess, waIntro: f.pickupWaIntro } };
  const c = copy[type] || copy.devis;
  useEffect(() => {
    setType(paramType);
    setSent(null);
    if (window.location.hash === "#formulaire") setTimeout(() => document.getElementById("formulaire")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  }, [paramType, params]);
  const [photos, setPhotos] = useState([]);
  const today = new Date().toISOString().slice(0, 10);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const waLink = (q) => {
    const period = q.pickup_period === "morning" ? f.periodMorning : q.pickup_period === "afternoon" ? f.periodAfternoon : "";
    const rdv = q.date ? `${q.date}${q.time ? ` ${q.time}` : period ? ` (${period})` : ""}` : "-";
    const pickup = q.request_type === "pickup" ? `\n${f.pickupAddress}: ${q.pickup_address}` : "";
    const when = q.request_type === "devis" ? "" : `\n${q.request_type === "pickup" ? f.pickupDate : f.date}: ${rdv}`;
    const intro = (copy[q.request_type] || copy.devis).waIntro;
    const text = `${intro}\n\n${f.name}: ${q.name}\n${f.phone}: ${q.phone}\n${f.service}: ${q.service}\n${f.vehicle}: ${q.vehicle || "-"}${pickup}${when}\n${(copy[q.request_type] || copy.devis).message}: ${q.message}`;
    return `${WHATSAPP_URL}?text=${encodeURIComponent(text)}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      let uploaded = [];
      if (photos.length) {
        const fd = new FormData();
        photos.forEach((p) => fd.append("files", p));
        const res = await axios.post(`${API}/quotes/photos`, fd);
        uploaded = res.data.map((p) => p.path);
      }
      const payload = { ...form, photos: uploaded, lang, request_type: type };
      if (isPickup) payload.time = "";
      if (isDevis) { payload.date = ""; payload.time = ""; payload.pickup_address = ""; payload.pickup_period = ""; }
      const res = await axios.post(`${API}/quotes`, payload);
      toast.success(c.success);
      setSent({ ...payload, tracking_code: res.data.tracking_code });
      setForm(empty);
      setPhotos([]);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(f.timeConflict);
        setForm({ ...form, time: "" });
        setSlotRefresh((n) => n + 1);
      } else {
        toast.error(f.error);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <Seo page="contact" />
      <section className="relative overflow-hidden pb-16 pt-40 sm:pb-20">
        <div className="pointer-events-none absolute -right-40 top-10 h-96 w-96 rounded-full bg-mint blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead as="h1" overline={t.contact.overline} title={t.contact.title} accent={t.contact.titleAccent} sub={t.contact.sub} />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-28 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-5 lg:gap-16">
          <Reveal className="lg:col-span-3">
            <div className="mb-8" id="formulaire">
              <RequestTypeSwitch value={type} onChange={(k) => { setType(k); setSent(null); }} />
              {prefilled && <p className="mt-4 rounded-2xl border border-gold/30 bg-cream px-4 py-3 text-sm text-forest" data-testid="contact-prefilled-notice">{f.prefilled}</p>}
            </div>
            {type === "question" ? <QuestionForm /> : sent ? (
              <div className="flex flex-col items-start gap-6 rounded-3xl border border-brand/10 bg-white p-8 shadow-xl shadow-brand/5 sm:p-12" data-testid="quote-sent-panel">
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-mint text-brand">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h2 className="font-display text-2xl font-extrabold text-forest sm:text-3xl">{(copy[sent.request_type] || copy.devis).success}</h2>
                {sent.tracking_code && (
                  <div className="w-full rounded-2xl bg-mint p-5" data-testid="quote-tracking-panel">
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand">{t.track.codeLabel}</p>
                    <p className="font-display mt-2 text-3xl font-extrabold tracking-[0.15em] text-gold" data-testid="quote-tracking-code">{sent.tracking_code}</p>
                    <p className="mt-2 text-sm text-brand-hover">{t.track.codeHint}</p>
                  </div>
                )}
                <p className="text-base text-brand-hover">{f.whatsappHint}</p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href={waLink(sent)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="quote-whatsapp-also-button"
                    className="inline-flex items-center gap-3 rounded-full bg-brand px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-lg shadow-brand/25 transition-all hover:-translate-y-0.5 hover:bg-brand-hover"
                  >
                    <MessageCircle className="h-4 w-4" />
                    {f.whatsappAlso}
                  </a>
                  <button
                    onClick={() => setSent(null)}
                    data-testid="quote-new-request-button"
                    className="inline-flex items-center gap-2 rounded-full border-2 border-gold px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-all hover:bg-gold hover:text-white"
                  >
                    {f.newRequest}
                  </button>
                </div>
              </div>
            ) : (
            <form onSubmit={onSubmit} className="space-y-5 rounded-3xl border border-brand/10 bg-white p-6 shadow-xl shadow-brand/5 sm:p-10" data-testid="quote-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>{f.name} *</label>
                  <input required value={form.name} onChange={set("name")} placeholder={f.namePh} data-testid="quote-form-name-input" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{f.phone} *</label>
                  <input required value={form.phone} onChange={set("phone")} placeholder={f.phonePh} data-testid="quote-form-phone-input" className={inputClass} />
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>{f.email}</label>
                  <input type="email" value={form.email} onChange={set("email")} placeholder={f.emailPh} data-testid="quote-form-email-input" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{f.vehicle}</label>
                  <input value={form.vehicle} onChange={set("vehicle")} placeholder={f.vehiclePh} data-testid="quote-form-vehicle-input" className={inputClass} />
                </div>
              </div>

              <div>
                <label className={labelClass}>{f.service} *</label>
                <select required value={form.service} onChange={set("service")} data-testid="quote-form-service-select" className={inputClass}>
                  <option value="" disabled>{f.servicePh}</option>
                  <optgroup label={f.groupServices}>
                    {t.services.items.map((s) => (
                      <option key={s.num} value={s.title}>{s.num} — {s.title}</option>
                    ))}
                  </optgroup>
                  <optgroup label={f.groupActivities}>
                    {t.activities.items.map((a) => (
                      <option key={a.title} value={a.title}>{a.title}</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {isPickup && (
                <div>
                  <label className={labelClass}>{f.pickupAddress} *</label>
                  <input required value={form.pickup_address} onChange={set("pickup_address")} placeholder={f.pickupAddressPh} data-testid="quote-form-pickup-address-input" className={inputClass} />
                </div>
              )}

              {!isDevis && (
              <div className={isPickup ? "grid gap-5 sm:grid-cols-2" : ""}>
                <div>
                  <label className={labelClass}>{isPickup ? f.pickupDate : f.date}</label>
                  <input type="date" min={today} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value, time: "" })} data-testid="quote-form-date-input" className={inputClass} />
                </div>
                {isPickup && (
                  <div>
                    <label className={labelClass}>{f.period}</label>
                    <select value={form.pickup_period} onChange={set("pickup_period")} data-testid="quote-form-period-select" className={inputClass}>
                      <option value="">{f.periodPh}</option>
                      <option value="morning">{f.periodMorning}</option>
                      <option value="afternoon">{f.periodAfternoon}</option>
                    </select>
                  </div>
                )}
              </div>
              )}

              {isPickup && <p className="rounded-xl bg-cream px-4 py-3 text-xs leading-relaxed text-forest" data-testid="quote-form-pickup-note">{f.pickupNote}</p>}
              {type === "rdv" && (
                <div>
                  <label className={labelClass}>{f.time}</label>
                  <SlotPicker date={form.date} value={form.time} onChange={(time) => setForm({ ...form, time })} labels={f} refreshKey={slotRefresh} />
                </div>
              )}

              <div>
                <label className={labelClass}>{c.message} *</label>
                <textarea required rows={5} value={form.message} onChange={set("message")} placeholder={c.messagePh} data-testid="quote-form-message-input" className={`${inputClass} resize-none`} />
                <AiWriteButton kind={isPickup ? "pickup" : "quote"} value={form.message} onText={(message) => setForm((p) => ({ ...p, message }))} context={{ service: form.service, vehicle: form.vehicle }} testid="quote-ai-write" />
              </div>

              <div>
                <label className={labelClass}>{f.photos}</label>
                <PhotoPicker files={photos} onChange={setPhotos} labels={f} />
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="quote-form-submit-button"
                className="group flex w-full items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-xl shadow-gold/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover disabled:opacity-60 sm:w-auto"
              >
                {sending ? (photos.length ? f.photosUploading : f.sending) : c.submit}
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
            )}
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-2">
            <div className="rounded-3xl bg-mint p-8 sm:p-10">
              <h2 className="font-display text-xl font-bold text-forest">{t.contact.info.title}</h2>
              <ul className="mt-8 space-y-7">
                <InfoRow icon={MapPin} label={t.contact.info.address} orange>
                  <p className="mt-1.5 text-sm text-forest">{st.address}</p>
                </InfoRow>
                <InfoRow icon={Phone} label={t.contact.info.phone}>
                  <a href={`tel:${st.phone.replace(/\s/g, "")}`} className="mt-1.5 block text-sm font-semibold text-forest transition-colors hover:text-gold">{st.phone}</a>
                  {(st.phones?.length ? st.phones : (st.phone2 ? [{ label: "", number: st.phone2 }] : [])).map((p, i) => (
                    <a key={i} href={`tel:${p.number.replace(/\s/g, "")}`} data-testid={`contact-phone-${i}`} className="mt-1 block text-sm font-semibold text-forest transition-colors hover:text-gold">{p.number}{p.label && <span className="ml-2 text-xs font-normal text-brand">{p.label}</span>}</a>
                  ))}
                </InfoRow>
                <InfoRow icon={Mail} label={t.contact.info.email} orange>
                  <a href={`mailto:${st.email}`} className="mt-1.5 block text-sm text-forest transition-colors hover:text-gold">{st.email}</a>
                </InfoRow>
                <InfoRow icon={Globe} label={t.contact.info.website}>
                  <a href={`https://${st.website.replace(/^https?:\/\//, "")}`} target="_blank" rel="noopener noreferrer" className="mt-1.5 block text-sm text-forest transition-colors hover:text-gold">{st.website}</a>
                </InfoRow>
                <InfoRow icon={Clock} label={t.contact.info.hours} orange>
                  <p className="mt-1.5 text-sm text-forest">{st.hours}</p>
                </InfoRow>
              </ul>

              <a
                href="#formulaire"
                onClick={(e) => { e.preventDefault(); setType("devis"); setSent(null); document.getElementById("formulaire")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                data-testid="contact-whatsapp-button"
                className="mt-9 flex items-center justify-center gap-3 rounded-full bg-brand px-6 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-lg shadow-brand/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-hover"
              >
                <MessageCircle className="h-4 w-4" />
                {t.contact.info.whatsCta}
              </a>
            </div>

            <div className="mt-6 overflow-hidden rounded-3xl border border-brand/10">
              <iframe
                title="GROUPE AUTO LUXE — Abidjan"
                src="https://maps.google.com/maps?q=Koumassi%20Remblais%2C%20Abidjan%2C%20C%C3%B4te%20d%27Ivoire&t=&z=14&ie=UTF8&iwloc=&output=embed"
                className="h-64 w-full"
                loading="lazy"
                data-testid="contact-map"
              />
            </div>
          </Reveal>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-6 pb-16 lg:px-12"><PaymentMethods testid="contact-payment-methods" /></section>
      {st.sections?.maps !== false && <LocationsMap />}
      {st.sections?.vcards !== false && <BusinessCards />}
    </main>
  );
};

export default Contact;
